import React, { useState, useEffect } from "react";
import {
  FaCode,
  FaGraduationCap,
  FaBullseye,
  FaRocket,
} from "react-icons/fa";
import { motion } from "framer-motion";

const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateStars = () => {
    const numberOfStars = Math.floor((window.innerWidth * window.innerHeight) / 8000);
    const newStars = Array.from({ length: numberOfStars }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      opacity: Math.random() * 0.6 + 0.3,
      speed: Math.random() * 2 + 1,
    }));
    setStars(newStars);
  };

  const generateMeteors = () => {
    const numberOfMeteors = 4;
    const newMeteors = Array.from({ length: numberOfMeteors }, (_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 1,
      x: Math.random() * 100,
      y: Math.random() * 20,
      delay: Math.random() * 10,
      animationDuration: Math.random() * 3 + 3,
    }));
    setMeteors(newMeteors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255, 255, 255, 0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${parallaxX * (star.speed / 2)}px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}

      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.animationDuration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}

      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes meteor {
          0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
          100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const aboutItems = [
  {
    icon: <FaRocket className="text-indigo-400 text-3xl" />,
    title: "Passionate Developer",
    description:
      "I thrive on building impactful software. My passion for technology drives me to constantly learn, experiment, and turn ideas into functional experiences.",
    border: "border-indigo-500/30",
    shadow: "hover:shadow-indigo-400/30",
    text: "text-indigo-400",
  },
  {
    icon: <FaCode className="text-purple-400 text-3xl" />,
    title: "Full-Stack Expertise",
    description:
      "Skilled in both frontend and backend, I create full-stack solutions using React, Node.js, Express, and MongoDB that are clean, scalable, and efficient.",
    border: "border-purple-500/30",
    shadow: "hover:shadow-purple-400/30",
    text: "text-purple-400",
  },
  {
    icon: <FaGraduationCap className="text-emerald-400 text-3xl" />,
    title: "Educational Background",
    description:
      "Currently pursuing B.Tech in Computer Science at Rai University, I’ve developed strong foundations in problem solving, data structures, and software engineering.",
    border: "border-emerald-500/30",
    shadow: "hover:shadow-emerald-400/30",
    text: "text-emerald-400",
  },
  {
    icon: <FaBullseye className="text-pink-400 text-3xl" />,
    title: "Future Goals",
    description:
      "Aspiring to become a creative and technically sound Tech Architect, leading impactful projects and empowering the developer community through mentorship.",
    border: "border-pink-400/30",
    shadow: "hover:shadow-pink-300/30",
    text: "text-pink-400",
  },
];

const timeline = [
  { year: "2024", event: "Started B.Tech in Computer Science at Rai University" },
  { year: "2025", event: "Built multiple full-stack projects using MERN stack" },
  { year: "2026", event: "Will do Internship in Tech Company" },
  { year: "2027", event: "Will focus on performance, testing, deployment, and DevOps" },
  { year: "2028+", event: "Aim to become a tech architect & team leader" },
];

const tabs = ["About", "Timeline"];

const About = () => {
  const [activeTab, setActiveTab] = useState("About");

  return (
    <section className="relative text-white px-4 sm:px-6 py-20 min-h-screen flex items-center justify-center overflow-hidden">
      <StarBackground />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-90 z-0" />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-6xl mx-auto text-center"
      >
        <h2 className="text-4xl font-extrabold mb-6 bg-gradient-to-r from-indigo-400 via-sky-400 to-pink-500 text-transparent bg-clip-text">
          About Me
        </h2>

        <div className="flex justify-center gap-6 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                activeTab === tab
                  ? "bg-white text-black"
                  : "bg-white/10 text-slate-300 hover:bg-white/20"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "About" && (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-left"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {aboutItems.map((item, index) => (
              <div
                key={index}
                className={`bg-white/5 backdrop-blur-md rounded-xl p-6 border ${item.border} ${item.shadow} hover:scale-[1.02] transition-all duration-300`}
              >
                <div className="flex items-center gap-4 mb-3">
                  {item.icon}
                  <h3 className={`text-lg font-semibold ${item.text}`}>{item.title}</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </motion.div>
        )}

        {activeTab === "Timeline" && (
          <motion.div
            className="text-left max-w-2xl mx-auto space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {timeline.map((item, idx) => (
              <div
                key={idx}
                className="border-l-4 border-purple-500 pl-4 relative"
              >
                <div className="absolute top-1 left-[-10px] w-4 h-4 bg-purple-500 rounded-full" />
                <h4 className="text-lg font-semibold text-purple-300">{item.year}</h4>
                <p className="text-slate-300">{item.event}</p>
              </div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default About;
